import { Box, Stack, Typography } from '@mui/material';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import type {
  DollarValuePerContract,
  EnrichedRegime,
  Forecast,
  RegimeResult,
  SearchCandidate,
  StructureLeg,
  TVTPOutput,
  VRPTermStructure,
} from 'gen/stream-api/requests';
import { convertToDaysTillExpiry, getForwardIV } from 'util/iVol';
import {
  getTVTPColor,
  num,
  pct,
  pct0,
  TVTP_REGIME_LABELS,
} from 'util/moviefoneFormat';
import { dayjs, ET, stockMarketClose } from 'util/shared/date';
import useReadableTextColors from 'hooks/moviefone/useReadableTextColors';

// Mirrors the backend's SURFACE_RESIDUAL_SIGNAL_THRESHOLD (sg-risk-engine moviefone_consts.rs).
const RESIDUAL_SIGNAL_THRESHOLD = 0.015;

const TVTP_REGIMES = [
  { key: 'LOW_VOL', label: 'Low vol' },
  { key: 'NORMAL', label: 'Normal' },
  { key: 'HIGH_VOL', label: 'High vol' },
  { key: 'CRISIS', label: 'Crisis' },
] as const;

const EMPTY = 'N/A';

const signedVolPts = (diff: number): string =>
  `${diff >= 0 ? '+' : ''}${(diff * 100).toFixed(1)}`;

const toDateKey = (date: string) => dayjs(date).format('YYYY-MM-DD');

const expiryMs = (expiry: string) =>
  stockMarketClose(dayjs.tz(expiry, ET)).valueOf();

interface LegIV {
  leg: StructureLeg;
  dte: number;
  current: number | null;
  projected: number | null;
}

const getLegIVs = (
  legs: StructureLeg[],
  dollarValueContracts: DollarValuePerContract[],
): LegIV[] => {
  const now = dayjs().valueOf();
  return legs
    .filter((leg) => leg.option_type !== 'Stock')
    .map((leg) => {
      const contract = dollarValueContracts.find(
        (c) =>
          toDateKey(c.expiration) === toDateKey(leg.expiry) &&
          c.strike === leg.strike &&
          c.is_call === (leg.option_type === 'Call'),
      );
      return {
        leg,
        dte: convertToDaysTillExpiry(now, expiryMs(leg.expiry)),
        current: leg.iv ?? null,
        projected: contract?.projected_iv ?? null,
      };
    });
};

const Panel = ({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        minWidth: 0,
        p: '10px 12px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.text.primary, 0.02),
      }}
    >
      {title != null && (
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
        >
          {title}
        </Typography>
      )}
      {children}
    </Box>
  );
};

const LEG_TABLE_COLUMNS = 'minmax(0, 1fr) repeat(4, auto)';

const LegTableRow = ({
  cells,
  isHeader = false,
  emphasize = false,
}: {
  cells: ReactNode[];
  isHeader?: boolean;
  emphasize?: boolean;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: LEG_TABLE_COLUMNS,
        alignItems: 'baseline',
        columnGap: '14px',
        py: '4px',
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {cells.map((cell, i) => (
        <Typography
          key={i}
          variant="caption"
          component="div"
          sx={{
            textAlign: i === 0 ? 'left' : 'right',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: isHeader ? '10px' : '12px',
            fontWeight: emphasize ? 700 : 400,
            color: isHeader
              ? theme.palette.text.secondary
              : theme.palette.text.primary,
          }}
        >
          {cell}
        </Typography>
      ))}
    </Box>
  );
};

const residualSignal = (current: number, projected: number, theme: Theme) => {
  const residual = current - projected;
  if (residual > RESIDUAL_SIGNAL_THRESHOLD) {
    return { label: 'rich', color: theme.palette.warning.main };
  }
  if (residual < -RESIDUAL_SIGNAL_THRESHOLD) {
    return { label: 'cheap', color: theme.palette.success.main };
  }
  return { label: 'fair', color: theme.palette.text.secondary };
};

const LegIVPanel = ({
  legIVs,
  forecast,
  showInternalOnly,
}: {
  legIVs: LegIV[];
  forecast: Forecast | undefined;
  showInternalOnly: boolean;
}) => {
  const theme = useTheme();
  const textColors = useReadableTextColors();
  const shortLegs = legIVs.filter((l) => l.leg.action === 'Sell');
  const longLegs = legIVs.filter((l) => l.leg.action === 'Buy');
  const spread =
    legIVs.length === 2 && shortLegs.length === 1 && longLegs.length === 1
      ? { short: shortLegs[0], long: longLegs[0] }
      : null;
  const [front, back] = [...legIVs].sort((a, b) => a.dte - b.dte);
  const forwardVol =
    legIVs.length === 2 &&
    front.dte !== back.dte &&
    front.current != null &&
    back.current != null
      ? getForwardIV(
          dayjs(),
          expiryMs(back.leg.expiry),
          expiryMs(front.leg.expiry),
          back.current,
          front.current,
        )
      : null;

  return (
    <Panel title="IV across legs">
      <LegTableRow
        isHeader
        cells={['Leg', 'Current IV', 'Projected IV', 'Change', 'Residual']}
      />
      {legIVs.map(({ leg, dte, current, projected }, i) => {
        const isShort = leg.action === 'Sell';
        const signal =
          current != null && projected != null
            ? residualSignal(current, projected, theme)
            : null;
        return (
          <LegTableRow
            key={i}
            cells={[
              <>
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: isShort ? textColors.error : textColors.sgGreen,
                  }}
                >
                  {isShort ? 'Short' : 'Long'}
                </Box>{' '}
                {leg.strike}
                {leg.option_type[0]} · {dte}d
              </>,
              current != null ? pct(current) : EMPTY,
              projected != null ? pct(projected) : EMPTY,
              current != null && projected != null
                ? signedVolPts(projected - current)
                : EMPTY,
              signal != null ? (
                <Box component="span" sx={{ color: signal.color }}>
                  {signal.label}
                </Box>
              ) : (
                EMPTY
              ),
            ]}
          />
        );
      })}
      {spread != null && (
        <LegTableRow
          emphasize
          cells={[
            'Spread (short − long)',
            spread.short.current != null && spread.long.current != null
              ? `${signedVolPts(spread.short.current - spread.long.current)} pts`
              : EMPTY,
            spread.short.projected != null && spread.long.projected != null
              ? `${signedVolPts(spread.short.projected - spread.long.projected)} pts`
              : EMPTY,
            '',
            '',
          ]}
        />
      )}
      {legIVs.length === 2 && front.dte !== back.dte && (
        <LegTableRow
          cells={[
            `Forward vol (${front.dte}d → ${back.dte}d)`,
            forwardVol != null && Number.isFinite(forwardVol)
              ? pct(forwardVol)
              : EMPTY,
            '',
            '',
            '',
          ]}
        />
      )}
      {showInternalOnly && (
        <LegTableRow
          cells={[
            'IV forecast (1d), direction',
            '',
            '',
            '',
            forecast != null
              ? `${forecast.direction} (${pct0(forecast.confidence)})`
              : EMPTY,
          ]}
        />
      )}
    </Panel>
  );
};

const KVRow = ({ label, value }: { label: string; value: ReactNode }) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{ py: '3px', borderBottom: `1px solid ${theme.palette.divider}` }}
    >
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary }}
      >
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Stack>
  );
};

const VolRegimeBar = ({ tvtp }: { tvtp: TVTPOutput }) => {
  const theme = useTheme();
  const currentKey = TVTP_REGIME_LABELS[tvtp.regime] ?? tvtp.regime;
  const currentIdx = TVTP_REGIMES.findIndex((r) => r.key === currentKey);
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Vol regime
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: getTVTPColor(currentKey, theme) }}
        >
          {TVTP_REGIMES[currentIdx]?.label ?? tvtp.regime}
          {currentIdx >= 0 && ` · ${pct0(tvtp.confidence)}`}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        sx={{ height: '8px', borderRadius: '4px', overflow: 'hidden' }}
      >
        {TVTP_REGIMES.map(({ key }, i) => (
          <Box
            key={key}
            sx={{
              width: `${(tvtp.regime_probabilities[i] ?? 0) * 100}%`,
              backgroundColor: getTVTPColor(key, theme),
            }}
          />
        ))}
      </Stack>
      <Stack
        direction="row"
        sx={{ flexWrap: 'wrap', columnGap: '12px', rowGap: '2px', mt: 0.75 }}
      >
        {TVTP_REGIMES.map(({ key, label }, i) => (
          <Stack
            key={key}
            direction="row"
            alignItems="center"
            sx={{ gap: '4px' }}
          >
            <Box
              sx={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: getTVTPColor(key, theme),
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontSize: '10px', color: theme.palette.text.secondary }}
            >
              {label} {pct0(tvtp.regime_probabilities[i])}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const ThesisRegimePanel = ({
  rationale,
  tvtp,
  showInternalOnly,
  enrichedRegime,
  regime,
  vrpTermStructure,
}: {
  rationale: string | undefined;
  tvtp: TVTPOutput | null | undefined;
  showInternalOnly: boolean;
  enrichedRegime: EnrichedRegime | undefined;
  regime: RegimeResult | undefined;
  vrpTermStructure: VRPTermStructure | null | undefined;
}) => (
  <Panel>
    <Stack sx={{ gap: '10px' }}>
      <Typography
        variant="body2"
        sx={{ fontSize: '12px', lineHeight: 1.45, color: 'text.primary' }}
      >
        <Box component="span" sx={{ fontWeight: 700 }}>
          Thesis:
        </Box>{' '}
        {rationale != null && rationale !== '' ? rationale : EMPTY}
      </Typography>
      {tvtp != null && <VolRegimeBar tvtp={tvtp} />}
      <Box>
        <KVRow
          label="IV rank"
          value={
            enrichedRegime?.iv_rank != null
              ? `${num(enrichedRegime.iv_rank, 0)}th pctl`
              : EMPTY
          }
        />
        <KVRow label="Term structure" value={regime?.term_structure ?? EMPTY} />
        {showInternalOnly && (
          <KVRow
            label="Richest / cheapest tenor"
            value={
              vrpTermStructure != null
                ? `${vrpTermStructure.richest_dte}d / ${vrpTermStructure.cheapest_dte}d`
                : EMPTY
            }
          />
        )}
      </Box>
    </Stack>
  </Panel>
);

interface MoviefoneOverviewSummaryProps {
  searchCandidate: SearchCandidate;
  dollarValueContracts: DollarValuePerContract[];
  forecast: Forecast | undefined;
  tvtp: TVTPOutput | null | undefined;
  // IV forecast direction and richest/cheapest tenor are internal-only on the rest of the card.
  showInternalOnly: boolean;
  enrichedRegime: EnrichedRegime | undefined;
  regime: RegimeResult | undefined;
  vrpTermStructure: VRPTermStructure | null | undefined;
}

const MoviefoneOverviewSummary = ({
  searchCandidate,
  dollarValueContracts,
  forecast,
  tvtp,
  showInternalOnly,
  enrichedRegime,
  regime,
  vrpTermStructure,
}: MoviefoneOverviewSummaryProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      gap: '12px',
    }}
  >
    <LegIVPanel
      legIVs={getLegIVs(searchCandidate.legs, dollarValueContracts)}
      forecast={forecast}
      showInternalOnly={showInternalOnly}
    />
    <ThesisRegimePanel
      rationale={searchCandidate.rationale}
      tvtp={tvtp}
      showInternalOnly={showInternalOnly}
      enrichedRegime={enrichedRegime}
      regime={regime}
      vrpTermStructure={vrpTermStructure}
    />
  </Box>
);

export default MoviefoneOverviewSummary;
